import { useMemo } from 'react';
import { Book } from '@/types/book';
import { useMultipleBookProgress } from '@/hooks/use-reading-tracker';

export interface BookWithProgress extends Book {
  lastPage: number;
  lastReadAt?: string;
  hasProgress: boolean;
  progressPercentage: number;
  estimatedTimeLeft: number; // in minutes
}

export function useBooksWithProgress(books: Book[]) {
  const { getBookProgress, isLoading, refreshProgress } = useMultipleBookProgress(books);

  // Memoize the expensive calculations to prevent re-computation
  const booksWithProgress = useMemo(() => {
    const enhancedBooks: BookWithProgress[] = books.map(book => {
      const progress = getBookProgress(Number(book.id));
      const lastPage = progress?.currentPage || 0;
      const hasProgress = progress?.hasProgress || false;
      
      // Calculate accurate total pages based on actual content structure
      const calculateTotalPages = () => {
        if (!book.description) return 100;
        
        const paragraphsPerPage = book.paragraphs_per_page || 3;
        
        // Split content into blocks and filter out empty ones
        const allBlocks = book.description
          .split(/\n\s*\n/)
          .filter(p => p.trim().length > 0)
          .map(p => p.trim());
        
        let totalPages = 0;
        let currentParagraphs = 0;
        
        for (const block of allBlocks) {
          // Enhanced chapter title detection
          const isChapterTitle = 
            block.match(/^chapter\s+\d+/i) || 
            block.match(/^chapter\s+\d+\s*:/i) ||
            (block.toLowerCase().includes('chapter') && block.length < 150) ||
            (block.match(/^[A-Z][^.!?]*$/) && block.length < 100);
          
          if (isChapterTitle) {
            // Complete current page if has content
            if (currentParagraphs > 0) {
              totalPages++;
              currentParagraphs = 0;
            }
            // Chapter title starts a new page
            totalPages++;
          } else {
            // Regular paragraph
            currentParagraphs++;
            if (currentParagraphs >= paragraphsPerPage) {
              totalPages++;
              currentParagraphs = 0;
            }
          }
        }
        
        // Add final page if there are remaining paragraphs
        if (currentParagraphs > 0) {
          totalPages++;
        }
        
        return Math.max(totalPages, 1);
      };
      
      const estimatedTotalPages = calculateTotalPages();
      
      const progressPercentage = hasProgress ? 
        Math.min((lastPage / estimatedTotalPages) * 100, 100) : 0;
      
      // Calculate more accurate reading time
      const calculateReadingTime = () => {
        if (!book.description) return 5;
        
        // Simple calculation: 30 seconds (0.5 minutes) per page
        const timePerPage = 0.5; // 30 seconds per page
        
        // Calculate remaining time based on progress
        const remainingPages = estimatedTotalPages - lastPage;
        const remainingTime = remainingPages * timePerPage;
        
        return Math.max(Math.round(remainingTime), 1);
      };
      
      const estimatedTimeLeft = calculateReadingTime();

      return {
        ...book,
        lastPage,
        lastReadAt: progress?.lastReadAt,
        hasProgress,
        progressPercentage,
        estimatedTimeLeft
      };
    });

    // Sort by reading priority: books with progress first (by recency), then by creation date
    enhancedBooks.sort((a, b) => {
      // Both have progress - sort by most recent
      if (a.hasProgress && b.hasProgress) {
        if (a.lastReadAt && b.lastReadAt) {
          return new Date(b.lastReadAt).getTime() - new Date(a.lastReadAt).getTime();
        }
        return a.lastReadAt ? -1 : 1;
      }
      
      // Only one has progress
      if (a.hasProgress && !b.hasProgress) return -1;
      if (!a.hasProgress && b.hasProgress) return 1;
      
      // Neither has progress - sort by creation date
      const aDate = new Date(a.created_at || 0).getTime();
      const bDate = new Date(b.created_at || 0).getTime();
      return bDate - aDate;
    });

    return enhancedBooks;
  }, [books, getBookProgress]); // Only recalculate when books or progress data changes

  return {
    booksWithProgress,
    isLoading,
    refreshProgress
  };
}